import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Container from "../../../components/commonContainer/Container";
import companyApiService from "../../services/companyApiService";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import BusinessIcon from "@mui/icons-material/Business";
import StoreIcon from "@mui/icons-material/Store";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { showSuccessToast, showErrorToast } from "../../../utils/toast/toast";
import CommonModal from "../../../components/commonModal/CommonModal";
import ModelBtn from "../../../components/commonModal/ModelBtn";
import InputSelect from "../../../components/inputs/InputSelect";
import "./ShopDetails.scss";

interface Owner {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: string;
  status: boolean;
}

interface Mart {
  id: number;
  name: string;
  phoneNumber: string;
  address: string;
  email?: string;
  logo?: string;
  registerNumber?: string;
}

interface Branch {
  id: number;
  branchName: string;
  phoneNumber: string;
  address: string;
  status: boolean;
  currency: string;
  Marts?: Mart[];
}

interface CompanyData {
  company: {
    id: number;
    companyName: string;
    logo?: string;
    createdAt: string;
    updatedAt: string;
  };
  owner: Owner;
  branches: Branch[];
  stats: {
    totalBranches: number;
    totalMarts: number;
  };
}

interface Plan {
  id: number;
  name: string;
  billingType: "monthly" | "yearly";
  amount: number;
  currency: string;
}

interface Subscription {
  id: number;
  companyId: number;
  planId: number;
  startDate: string;
  endDate: string;
  status: "pending" | "active" | "expired" | "cancelled";
  autoRenew: boolean;
  Plan: Plan;
}

const ShopDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assignPlanOpen, setAssignPlanOpen] = useState(false);

  // Form state for assigning plan
  const [planForm, setPlanForm] = useState({
    planId: "",
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
    status: "active",
    autoRenew: false,
  });

  useEffect(() => {
    const fetchShopDetails = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await companyApiService.getCompanyDetails(id!);
        console.log("Company Details:", response);
        setCompanyData(response.data);

        // Fetch subscription
        const subResponse = await companyApiService.getCompanySubscription(id!);
        if (subResponse.data.subscription) {
          setSubscription(subResponse.data.subscription);
        }

        // Fetch active plans
        const plansResponse = await companyApiService.getActivePlans();
        setPlans(plansResponse.data.plans || []);
      } catch (err) {
        setError("Failed to fetch company details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchShopDetails();
    }
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleOpenAssignPlan = () => {
    if (subscription) {
      setPlanForm({
        planId: subscription.planId.toString(),
        startDate: subscription.startDate,
        endDate: subscription.endDate,
        status: subscription.status,
        autoRenew: subscription.autoRenew,
      });
    } else {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1); // Default to 1 month

      setPlanForm({
        planId: plans[0]?.id.toString() || "",
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
        status: "active",
        autoRenew: false,
      });
    }
    setAssignPlanOpen(true);
  };

  const handleCloseAssignPlan = () => {
    setAssignPlanOpen(false);
  };

  const handleAssignPlan = async () => {
    try {
      const subscriptionData = {
        companyId: parseInt(id!),
        planId: parseInt(planForm.planId),
        startDate: planForm.startDate,
        endDate: planForm.endDate,
        status: planForm.status,
        autoRenew: planForm.autoRenew,
      };

      await companyApiService.assignPlanToCompany(subscriptionData);
      showSuccessToast("Plan assigned successfully");

      // Refresh subscription data
      const subResponse = await companyApiService.getCompanySubscription(id!);
      if (subResponse.data.subscription) {
        setSubscription(subResponse.data.subscription);
      }

      handleCloseAssignPlan();
    } catch (err: any) {
      console.error("Failed to assign plan:", err);
      showErrorToast(err.response?.data?.message || "Failed to assign plan");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "status-active";
      case "expired":
        return "status-expired";
      case "cancelled":
        return "status-cancelled";
      default:
        return "status-pending";
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="loading-container">
          <h3>Loading company details...</h3>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="error-container">
          <p style={{ color: "red" }}>{error}</p>
          <button
            onClick={() => navigate("/company/shops")}
            className="back-btn"
          >
            Back to Shops
          </button>
        </div>
      </Container>
    );
  }

  if (!companyData) {
    return (
      <Container>
        <div className="not-found-container">
          <h3>Company not found.</h3>
          <button
            onClick={() => navigate("/company/shops")}
            className="back-btn"
          >
            Back to Shops
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="shop-details-page">
      {/* Header */}
      <div className="details-header">
        <button
          onClick={() => navigate("/company/shops")}
          className="back-button"
        >
          <ArrowBackIcon />
          Back
        </button>
        <h1>{companyData.company.companyName}</h1>
        <button className="edit-button">
          <BorderColorIcon />
          Edit Company
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <BusinessIcon className="stat-icon company" />
          <div className="stat-content">
            <h3>{companyData.stats.totalBranches}</h3>
            <p>Total Branches</p>
          </div>
        </div>
        <div className="stat-card">
          <StoreIcon className="stat-icon mart" />
          <div className="stat-content">
            <h3>{companyData.stats.totalMarts}</h3>
            <p>Total Marts</p>
          </div>
        </div>
      </div>

      {/* Owner Information */}
      {/* <div className="section-card">
        <div className="section-header">
          <PersonIcon />
          <h2>Owner Information</h2>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <PersonIcon className="info-icon" />
            <div>
              <label>Name</label>
              <p>{`${companyData.owner.firstName} ${companyData.owner.lastName}`}</p>
            </div>
          </div>
          <div className="info-item">
            <EmailIcon className="info-icon" />
            <div>
              <label>Email</label>
              <p>{companyData.owner.email}</p>
            </div>
          </div>
          <div className="info-item">
            <PhoneIcon className="info-icon" />
            <div>
              <label>Phone</label>
              <p>{companyData.owner.phoneNumber}</p>
            </div>
          </div>
          <div className="info-item">
            <LocationOnIcon className="info-icon" />
            <div>
              <label>Address</label>
              <p>{companyData.owner.address}</p>
            </div>
          </div>
          <div className="info-item">
            <div className={`status-badge ${companyData.owner.status ? "active" : "inactive"}`}>
              {companyData.owner.status ? "Active" : "Inactive"}
            </div>
          </div>
        </div>
      </div> */}

      {/* Company Information */}
      <div className="section-card">
        <div className="section-header">
          <BusinessIcon />
          <h2>Company Information</h2>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <CalendarTodayIcon className="info-icon" />
            <div>
              <label>Created On</label>
              <p>{formatDate(companyData.company.createdAt)}</p>
            </div>
          </div>
          <div className="info-item">
            <CalendarTodayIcon className="info-icon" />
            <div>
              <label>Last Updated</label>
              <p>{formatDate(companyData.company.updatedAt)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Information */}
      <div className="section-card subscription-card">
        <div className="section-header">
          <SubscriptionsIcon />
          <h2>Subscription Plan</h2>
          <button className="assign-plan-btn" onClick={handleOpenAssignPlan}>
            <BorderColorIcon />
            {subscription ? "Update Plan" : "Assign Plan"}
          </button>
        </div>

        {subscription ? (
          <div className="subscription-details">
            <div className="subscription-header">
              <div className="plan-info">
                <h3>{subscription.Plan.name}</h3>
                <div className="plan-price">
                  {subscription.Plan.currency} {subscription.Plan.amount} /{" "}
                  {subscription.Plan.billingType}
                </div>
              </div>
              <div
                className={`subscription-status ${getStatusColor(subscription.status)}`}
              >
                {subscription.status === "active" ? (
                  <CheckCircleIcon />
                ) : (
                  <CancelIcon />
                )}
                <span>{subscription.status.toUpperCase()}</span>
              </div>
            </div>

            <div className="subscription-info-grid">
              <div className="info-item">
                <CalendarTodayIcon className="info-icon" />
                <div>
                  <label>Start Date</label>
                  <p>{formatDate(subscription.startDate)}</p>
                </div>
              </div>
              <div className="info-item">
                <CalendarTodayIcon className="info-icon" />
                <div>
                  <label>End Date</label>
                  <p>{formatDate(subscription.endDate)}</p>
                </div>
              </div>
              <div className="info-item">
                <div>
                  <label>Auto Renew</label>
                  <p>{subscription.autoRenew ? "Yes" : "No"}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <SubscriptionsIcon className="empty-icon" />
            <p>No subscription plan assigned</p>
            <button className="assign-btn-empty" onClick={handleOpenAssignPlan}>
              Assign a Plan
            </button>
          </div>
        )}
      </div>

      {/* Branches & Marts */}
      <div className="section-card">
        <div className="section-header">
          <BusinessIcon />
          <h2>Branches & Marts</h2>
        </div>

        {companyData.branches.length === 0 ? (
          <div className="empty-state">
            <BusinessIcon className="empty-icon" />
            <p>No branches found for this company</p>
          </div>
        ) : (
          <div className="branches-container">
            {companyData.branches.map((branch) => (
              <div key={branch.id} className="branch-card">
                <div className="branch-header">
                  <h3>{branch.branchName}</h3>
                  <span
                    className={`status-badge ${branch.status ? "active" : "inactive"}`}
                  >
                    {branch.status ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="branch-info">
                  <div className="info-row">
                    <PhoneIcon className="small-icon" />
                    <span>{branch.phoneNumber}</span>
                  </div>
                  <div className="info-row">
                    <LocationOnIcon className="small-icon" />
                    <span>{branch.address}</span>
                  </div>
                  <div className="info-row">
                    <span className="currency-badge">{branch.currency}</span>
                  </div>
                </div>

                {/* Marts under this branch */}
                {branch.Marts && branch.Marts.length > 0 && (
                  <div className="marts-section">
                    <h4>
                      <StoreIcon className="small-icon" />
                      Marts ({branch.Marts.length})
                    </h4>
                    <div className="marts-grid">
                      {branch.Marts.map((mart) => (
                        <div key={mart.id} className="mart-item">
                          <div className="mart-name">{mart.name}</div>
                          <div className="mart-details">
                            <PhoneIcon className="tiny-icon" />
                            {mart.phoneNumber}
                          </div>
                          {mart.registerNumber && (
                            <div className="mart-register">
                              Reg: {mart.registerNumber}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assign Plan Modal */}
      <CommonModal
        open={assignPlanOpen}
        setopen={handleCloseAssignPlan}
        title={
          subscription ? "Update Subscription Plan" : "Assign Subscription Plan"
        }
      >
        <div className="assign-plan-form">
          <InputSelect
            label="Select Plan"
            value={planForm.planId}
            onChange={(e: any) =>
              setPlanForm({ ...planForm, planId: e.target.value })
            }
            option={plans.map((plan) => ({
              value: plan.id.toString(),
              name: `${plan.name} - ${plan.currency} ${plan.amount} / ${plan.billingType}`,
            }))}
            required
          />

          <div className="date-row">
            <div className="date-field">
              <label>Start Date</label>
              <input
                type="date"
                value={planForm.startDate}
                onChange={(e) =>
                  setPlanForm({ ...planForm, startDate: e.target.value })
                }
              />
            </div>
            <div className="date-field">
              <label>End Date</label>
              <input
                type="date"
                value={planForm.endDate}
                onChange={(e) =>
                  setPlanForm({ ...planForm, endDate: e.target.value })
                }
              />
            </div>
          </div>

          <InputSelect
            label="Status"
            value={planForm.status}
            onChange={(e: any) =>
              setPlanForm({ ...planForm, status: e.target.value })
            }
            option={[
              { value: "pending", name: "Pending" },
              { value: "active", name: "Active" },
              { value: "expired", name: "Expired" },
              { value: "cancelled", name: "Cancelled" },
            ]}
            required
          />

          <div className="checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={planForm.autoRenew}
                onChange={(e) =>
                  setPlanForm({ ...planForm, autoRenew: e.target.checked })
                }
              />
              Auto Renew
            </label>
          </div>

          <ModelBtn
            btnText1={subscription ? "Update Plan" : "Assign Plan"}
            handleClose={handleCloseAssignPlan}
            handleClick={handleAssignPlan}
          />
        </div>
      </CommonModal>
    </Container>
  );
};

export default ShopDetails;
